import { useId, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatClock } from 'src/lib/time';
import type { ResolvedClaim } from 'src/lib/resolveClaims';

/**
 * The claim marker under a paragraph of a drafted note.
 *
 * A `title` attribute holding a list of hashes was the old behaviour and it answered nothing: an
 * editor asking "which claim?" wants the sentence, the speaker and the line from the guion, not an
 * identifier. Art Direction §5 — the route to the evidence is part of the same visual unit.
 *
 * Click, not hover: a hover tooltip is unreachable on a phone and unreadable by a screen reader,
 * and this is the affordance that makes the note checkable.
 */
export function ClaimMarker({
  claimIds,
  streamId,
  resolve,
  onOpen,
  loading,
}: {
  claimIds: string[];
  streamId: string;
  resolve: (claimId: string) => ResolvedClaim | undefined;
  /** Called the first time any marker opens, so the page can fetch the claim sources lazily. */
  onOpen: () => void;
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  if (claimIds.length === 0) {
    return (
      <span className="r-claims none" title="Este párrafo no cita ningún claim">
        sin claim
      </span>
    );
  }

  function toggle() {
    if (!open) onOpen();
    setOpen(value => !value);
  }

  return (
    <span className="claim-marker" data-open={open}>
      <button
        type="button"
        className="r-claims claim-marker__trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
      >
        {claimIds.length} claim{claimIds.length > 1 ? 's' : ''}
        <span className="claim-marker__hint">{open ? 'ocultar' : 'ver'}</span>
      </button>

      {open && (
        <span className="claim-marker__panel" id={panelId} role="group">
          {loading && <span className="claim-marker__loading">Cargando los claims…</span>}
          {!loading &&
            claimIds.map(claimId => {
              const claim = resolve(claimId);
              if (!claim) {
                return (
                  <span key={claimId} className="claim-marker__claim claim-marker__claim--missing">
                    <span className="claim-marker__id mono">{claimId}</span>
                    <span className="claim-marker__text">
                      Este identificador no corresponde a ningún claim de esta transmisión. La nota se generó antes de
                      que el hilo del claim quedara cerrado, así que el vínculo no se puede reconstruir.
                    </span>
                  </span>
                );
              }

              return (
                <span key={claimId} className="claim-marker__claim">
                  <span className="claim-marker__meta">
                    <Link className="claim-marker__id mono" to={`/stream/${encodeURIComponent(streamId)}/claim/${claimId}`}>
                      {claimId}
                    </Link>
                    <span className="claim-marker__who">
                      {claim.speaker || 'Hablante desconocido'}
                      {claim.timestamp && ` · ${formatClock(claim.timestamp)}`}
                    </span>
                  </span>

                  {/* Judgment — the canonical affirmation, interface face. */}
                  <span className="claim-marker__text">{claim.text}</span>

                  {/* Testimony — the words as they were said, reading face (Art Direction §6). */}
                  {claim.excerpt ? (
                    <span className="claim-marker__excerpt reading">“{claim.excerpt}”</span>
                  ) : (
                    <span className="claim-marker__no-excerpt">
                      Sin la cita literal del guion — este claim viene de la prenota, no de la normalización.
                    </span>
                  )}
                </span>
              );
            })}
        </span>
      )}
    </span>
  );
}
